import axiosMonetoring from 'config/axiosMonetoring';
import axiosAqisisFractal from 'config/axiosAqisisFractal';
import {
  transformData,
  formatKeywordData,
  transformToSemanticGap,
  normalizeSemanticGapList,
} from './FormatKeywordsHelper';

const AQISIS_AUTH_HEADERS = {
  Authorization: 'Bearer 0620dd19-6f38-303e-b6f5-e11c92f06941',
};

const isNotFound404 = (data, status) =>
  status === 404 ||
  data?.ErrorCode === 404 ||
  data?.Error ===
    "This keyword doesn't exist in AQISIS for this account>website." ||
  data?.Error === "This keyword doesn't exist for this account>website.";

const splitCommaSeparated = (value) =>
  String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const buildAqisisGapUrl = ({ account, keyword, headkeys, url, orderWebsite }) => {
  const keywordParts = splitCommaSeparated(keyword);
  const headkeyParts = splitCommaSeparated(headkeys);

  const keywordParam = keywordParts
    .map((key) => `keyword=${encodeURIComponent(key)}`)
    .join('&');
  const headkeysParam = headkeyParts
    .map((key) => `headkeys=${encodeURIComponent(key)}`)
    .join('&');
  const urlParam = url && url.trim() ? encodeURIComponent(url.trim()) : '';

  return `/get_all_upper_keywords_for_typix?${[
    `account=${encodeURIComponent(account)}`,
    headkeysParam,
    keywordParam,
    `url=${urlParam}`,
    `webUrl=${encodeURIComponent(orderWebsite.trim())}`,
  ]
    .filter(Boolean)
    .join('&')}`;
};

const fetchFractalAqisisGap = async ({
  constructedUrl,
  onError,
  onSuccess,
  onKeywordNotFound,
  onValidationError,
  t,
}) => {
  console.log('[Aqisis GAP] Fallback fractal API (after 404)', {
    constructedUrl,
  });

  try {
    const response = await axiosAqisisFractal.get(constructedUrl, {
      headers: AQISIS_AUTH_HEADERS,
    });
    const apiData = response?.data;

    if (apiData?.Error || apiData?.ErrorCode) {
      handleFractalError(apiData, apiData?.ErrorCode, {
        onError,
        onKeywordNotFound,
        onValidationError,
        t,
      });
      return;
    }

    if (
      apiData?.result &&
      Array.isArray(apiData.result) &&
      apiData.result.length > 0
    ) {
     
      const gapList = normalizeSemanticGapList(apiData.result);
      const gapTextFromApi = formatKeywordData(gapList);

      if (onSuccess) {
        onSuccess({ gapText: gapTextFromApi, gapList });
      }
      return;
    }

    if (onError) {
      onError(t('Aucun résultat trouvé pour ce mot-clé dans AQISIS.'));
    }
  } catch (error) {
    console.error('Erreur fallback fractal Aqisis GAP:', error);
    handleFractalError(error?.response?.data, error?.response?.status, {
      onError,
      onKeywordNotFound,
      onValidationError,
      t,
    });
  }
};

const handleFractalError = (
  errorData,
  status,
  { onError, onKeywordNotFound, onValidationError, t }
) => {
  const errorMessage = errorData?.Error || '';
  const errorCode = errorData?.ErrorCode ?? status;

  if (
    errorCode === 409 ||
    errorMessage ===
      'The semantic gap of this keyword has not been validated yet. Please try again later.'
  ) {
    if (onValidationError) onValidationError();
    return;
  }

  if (
    errorCode === 404 &&
    (errorMessage === "This keyword doesn't exist for this account>website." ||
      errorMessage ===
        "This keyword doesn't exist in AQISIS for this account>website." ||
      errorMessage.includes("doesn't exist"))
  ) {
    if (onKeywordNotFound) onKeywordNotFound();
    return;
  }

  if (
    errorCode === 404 ||
    errorMessage === 'Headkey not found for this keyword/url.'
  ) {
    if (onError) {
      onError(
        t(
          'Question (headkey) introuvable pour ce mot-clé/URL sur AQISIS. Veuillez vérifier avec le support Typix : support-typix@pixalione.com'
        )
      );
    }
    return;
  }

  if (
    errorCode === 400 &&
    errorMessage ===
      'Many landing pages found for this keyword. Please specify url.'
  ) {
    if (onError) {
      onError(
        t(
          "Plusieurs pages d'atterrissage ont été trouvées pour ce mot-clé. Veuillez préciser l'URL de la page."
        )
      );
    }
    return;
  }

  if (errorCode === 400 && errorMessage === 'keyword is required.') {
    if (onError) {
      onError(
        t('Veuillez renseigner un mot-clé avant de lancer le GAP from Aqisis.')
      );
    }
    return;
  }

  if (
    errorCode === 500 ||
    errorMessage === "Can't connect to the database; please try again."
  ) {
    if (onError) {
      onError(
        t(
          'Impossible de se connecter à la base de données AQISIS. Veuillez réessayer.'
        )
      );
    }
    return;
  }

  if (onError) {
    onError(
      errorData?.message ||
        errorMessage ||
        t(
          "Oups....! Une erreur s'est produite lors du calcul du GAP from Aqisis."
        )
    );
  }
};

export const fetchAqisisGap = async ({
  keyword,
  orderWebsite,
  url, 
  headkeys,
  onError,
  onSuccess,
  onKeywordNotFound,
  onWebsiteNotFound,
  onValidationError,
  setLoading,
  t,
}) => {
  console.log('[Aqisis GAP] Started', { keyword, orderWebsite, headkeys });

  // Get account from localStorage
  const rawUserData = localStorage.getItem('user');
  const userData = rawUserData ? JSON.parse(rawUserData) : null;
  const account = userData?.user?.companyName;

  // Validation
  if (
    !account ||
    !keyword ||
    !keyword.trim() ||
    !orderWebsite ||
    !orderWebsite.trim()
  ) {
    if (onError) {
      onError(
        t(
          'Veuillez renseigner un mot-clé avant de lancer le GAP from Aqisis.'
        )
      );
    }
    return;
  }

  const constructedUrl = buildAqisisGapUrl({
    account,
    keyword,
    headkeys,
    url,
    orderWebsite,
  });

  const fractalFallback = () =>
    fetchFractalAqisisGap({
      constructedUrl,
      onError,
      onSuccess,
      onKeywordNotFound,
      onValidationError,
      t,
    });

  try {
    if (setLoading) {
      setLoading(true);
    }
    if (onError) {
      onError('');
    }

    console.log('[Aqisis GAP] Request (ancienne API)', { constructedUrl });

    // 1) Toujours appeler l'ancienne API en premier
    const response = await axiosMonetoring.get(constructedUrl, {
      headers: AQISIS_AUTH_HEADERS,
    });

    const apiData = response?.data;

    // 2) Uniquement si 404 → appel de la nouvelle API
    if (isNotFound404(apiData, response?.status)) {
      await fractalFallback();
      return;
    }

    if (
      apiData?.Error ===
        "The website or the account doesn't exist in AQISIS." ||
      apiData?.ErrorCode === 418
    ) {
      if (onWebsiteNotFound) {
        onWebsiteNotFound();
      }
      return;
    }

    // Check for validation error
    if (
      apiData?.Error ===
      'The semantic gap of this keyword has not been validated yet. Please try again later.'
    ) {
      if (onValidationError) {
        onValidationError();
      }
      return;
    }

    if (
      apiData?.result &&
      Array.isArray(apiData.result) &&
      apiData.result.length > 0
    ) {
      const transformed = transformData(apiData);
      const gapTextFromApi = formatKeywordData(transformed || []);
      const gapList = transformToSemanticGap(gapTextFromApi || '');

      if (onSuccess) {
        onSuccess({
          gapText: gapTextFromApi,
          gapList: gapList,
        });
      }
    } else {
      // No results found
      if (onError) {
        onError(t('Aucun résultat trouvé pour ce mot-clé dans AQISIS.'));
      }
    }
  } catch (error) {
    console.error('Erreur lors de la récupération du GAP from Aqisis:', error);

    const errorData = error?.response?.data;
    const status = error?.response?.status;

    // 2) Uniquement si 404 → appel de la nouvelle API
    if (isNotFound404(errorData, status)) {
      await fractalFallback();
      return;
    }

    if (
      errorData?.Error ===
        "The website or the account doesn't exist in AQISIS." ||
      errorData?.ErrorCode === 418
    ) {
      if (onWebsiteNotFound) {
        onWebsiteNotFound();
      }
      return;
    }

    if (
      errorData?.Error ===
      'The semantic gap of this keyword has not been validated yet. Please try again later.'
    ) {
      if (onValidationError) {
        onValidationError();
      }
      return;
    }

    // Generic error
    if (onError) {
      onError(
        errorData?.message ||
          t(
            "Oups....! Une erreur s'est produite lors du calcul du GAP from Aqisis."
          )
      );
    }
  } finally {
    if (setLoading) {
      setLoading(false);
    }
  }
};
